/**
 * 宿主 <-> widget 的 postMessage 协议（版本 1）。
 *
 * 约束来源：CONTRACT.md 第四节 iframe 接入要求（固定 targetOrigin、必须同时校验
 * event.origin 与 event.source、requestId 关联、握手版本协商），
 * 以及需求第六/七节（上下文按实例隔离、身份切换清理）。
 *
 * 设计原则：
 * - 每帧都是 {ns, v, type, requestId?, payload?}，ns + v 必须精确匹配，未知类型直接丢弃；
 * - 响应帧必须带 requestId 才能被关联（避免"猜到的 ack"影响状态）；
 * - 客户端不可自称"账套已核实"：payload 里 companyVerified === true 视为 schema 违规；
 * - 解析函数只做纯校验，不产生任何副作用；不通过就返回 null，由调用方记录并忽略。
 */

import { isCanonicalDecimal, isUuid } from '@support/platform';

export const SUPPORT_WIDGET_NAMESPACE = 'support.widget';

/** 本版本实现的协议版本。 */
export const SUPPORT_WIDGET_PROTOCOL_VERSION = '1';

/** 双方可接受的协议版本白名单。 */
export const SUPPORT_WIDGET_SUPPORTED_VERSIONS: readonly string[] = ['1'];

/**
 * 后端 approvedFilters 白名单（support/internal/domain/message.go）：只有这些业务筛选键
 * 会进入服务端快照。SDK 侧必须在**出站前**就按同一口径过滤，不能依赖后端收到后再剔除。
 *
 * 与 @support/client 的 `WIRE_APPROVED_FILTER_KEYS` 同口径；两个包各自独立声明，
 * 避免为一个小常量引入跨包依赖（client 只依赖 @support/platform）。
 */
export const SUPPORT_CONTEXT_FILTER_KEYS: readonly string[] = [
  'keyword',
  'module',
  'period',
  'status',
  'type',
];

const APPROVED_FILTER_KEYS: ReadonlySet<string> = new Set(SUPPORT_CONTEXT_FILTER_KEYS);

/** 凭据样式键名（与 Go context.go 的保留字策略一致）：token/password/secret/cookie/authorization。 */
export function isCredentialKey(key: string): boolean {
  return /(token|password|secret|cookie|authorization)/u.test(key.toLowerCase());
}

/** 协议允许的筛选键：必须在白名单内，且不是凭据样式键。 */
export function isApprovedFilterKey(key: string): boolean {
  return APPROVED_FILTER_KEYS.has(key) && !isCredentialKey(key);
}

/** 页面稳定标识（与 Go 的 stableID 同口径）：绝不放行 URL/查询串/空白。 */
const STABLE_ID = /^[a-zA-Z0-9_.:-]{1,100}$/u;

export function isStablePageId(value: unknown): value is string {
  return typeof value === 'string' && STABLE_ID.test(value);
}

/** SDK 侧账期：YYYY-MM（年 1000..2999）。 */
const SDK_PERIOD = /^[12][0-9]{3}-(0[1-9]|1[0-2])$/u;
/** wire 侧账期：YYYYMM（同一套年份/月份约束）。 */
const COMPACT_PERIOD = /^[12][0-9]{3}(0[1-9]|1[0-2])$/u;

/** 账期必须是 [12]xxx 年 + 合法月（SDK 的 YYYY-MM 或 wire 的 YYYYMM）。 */
export function isPagePeriod(value: unknown): value is string {
  return typeof value === 'string' && (SDK_PERIOD.test(value) || COMPACT_PERIOD.test(value));
}

/** uint64 上限：Go 用 strconv.ParseUint(..., 64)，越界必被拒（1901003）。 */
const MAX_UINT64 = 18_446_744_073_709_551_615n;
const MAX_UINT64_DIGITS = '18446744073709551615'.length;

/** 非零规范十进制字符串且不超过 uint64。 */
export function isNonZeroUint64Decimal(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > MAX_UINT64_DIGITS) return false;
  if (!isCanonicalDecimal(value) || value === '0') return false;
  return BigInt(value) <= MAX_UINT64;
}

/** 页面上下文（宿主上报 / widget 侧快照），字段与 @support/client 的 PageContext 结构一致。 */
export interface SupportPageContext {
  clientReported?: boolean;
  /** 未核实/未选择时显式为 null。 */
  companyId?: null | string;
  /** 核实只能来自服务端权威来源；浏览器侧恒为 false。 */
  companyVerified?: boolean;
  collectedAt: string;
  filters?: Record<string, string>;
  frontendVersion?: string;
  lastErrorCode?: null | string;
  module?: string;
  pageInstanceId: string;
  pageKey: string;
  pageTitle?: string;
  period?: null | string;
  product?: string;
  receivedAt?: null | string;
  routeName?: string;
  selectedObjectId?: null | string;
  tab?: string;
  traceId?: null | string;
  version: string;
}

/** 宿主可提供的页面字段（不含版本/实例/时间戳，由 SDK 生成）。 */
export type SupportPageContextFields = Omit<
  SupportPageContext,
  'clientReported' | 'collectedAt' | 'pageInstanceId' | 'receivedAt' | 'version'
>;

export interface SupportPrincipalRef {
  id: string;
  name: string;
  orgId: string;
  role: 'admin' | 'agent' | 'customer' | 'supervisor';
  scopeId: string;
}

export type HostToWidgetType =
  | 'host.context'
  | 'host.handshake'
  | 'host.logout'
  | 'host.open'
  | 'host.preview'
  | 'host.session';

export type WidgetToHostType =
  | 'widget.capture'
  | 'widget.event'
  | 'widget.handshake.ack'
  | 'widget.handshake.error'
  | 'widget.preview'
  | 'widget.ready'
  | 'widget.request'
  | 'widget.status';

export interface ProtocolMessage<TType extends string> {
  ns: string;
  payload?: Record<string, unknown>;
  requestId?: string;
  type: TType;
  v: string;
}

export type HostToWidgetMessage = ProtocolMessage<HostToWidgetType>;
export type WidgetToHostMessage = ProtocolMessage<WidgetToHostType>;

/**
 * 宿主在握手时声明的可选能力。
 * screen-capture：宿主能在截图时临时隐藏客服窗口、框选时铺满整页、结束后恢复（见 widget.capture），
 * 且 iframe 带 allow="display-capture"。没声明时 widget 不显示截图按钮。
 */
export const HOST_CAPABILITY_SCREEN_CAPTURE = 'screen-capture';

export interface HostHandshakePayload extends Record<string, unknown> {
  /** 宿主声明的可选能力；旧宿主不带。 */
  capabilities?: string[];
  context: null | SupportPageContext;
  pageInstanceId: string;
  protocolVersion: string;
  sourceToken: null | string;
}

export interface HostContextPayload extends Record<string, unknown> {
  context: SupportPageContext;
  pageInstanceId: string;
  version: string;
}

export interface HostOpenPayload extends Record<string, unknown> {
  open: boolean;
}

export interface HostSessionPayload extends Record<string, unknown> {
  reason: 'login' | 'switch';
  sourceToken: string;
}

export interface WidgetReadyPayload extends Record<string, unknown> {
  protocolVersion: string;
  supportedProtocolVersions: string[];
  widgetVersion?: string;
}

export interface WidgetHandshakeAckPayload extends Record<string, unknown> {
  accepted: boolean;
  principal?: null | SupportPrincipalRef;
  protocolVersion: string;
}

export interface WidgetHandshakeErrorPayload extends Record<string, unknown> {
  message?: string;
  reason: 'internal' | 'protocol-version' | 'schema' | 'unauthorized';
}

export interface WidgetStatusPayload extends Record<string, unknown> {
  errorKind?: string;
  errorMessage?: string;
  open?: boolean;
  state: 'authenticated' | 'error' | 'idle' | 'unauthenticated';
  unread?: number;
}

export interface WidgetEventPayload extends Record<string, unknown> {
  accepted?: boolean;
  conversationId?: string;
  kind: 'context.applied' | 'conversation.changed' | 'message.received';
  messageId?: string;
  sequence?: string;
}

/**
 * widget 截图时请宿主切换客服窗口的显示：hide 临时隐藏（截图期间）、select 铺满整页（框选区域）、
 * restore 恢复原来的位置和大小。客服窗口保持“打开”状态，不触发关闭。
 */
export interface WidgetCapturePayload extends Record<string, unknown> {
  phase: 'hide' | 'restore' | 'select';
}

export interface WidgetRequestPayload extends Record<string, unknown> {
  capability: 'context' | 'token';
  reason?: string;
}

/** 客户在悬浮窗里点图：open 把原图交给宿主在页面上浮出，close 关掉这层。 */
export interface WidgetPreviewPayload {
  image?: Blob;
  mime?: string;
  name?: string;
  phase: 'close' | 'open';
}

const HOST_TYPES: ReadonlySet<string> = new Set<HostToWidgetType>([
  'host.context',
  'host.handshake',
  'host.logout',
  'host.open',
  'host.preview',
  'host.session',
]);

const WIDGET_TYPES: ReadonlySet<string> = new Set<WidgetToHostType>([
  'widget.capture',
  'widget.event',
  'widget.handshake.ack',
  'widget.handshake.error',
  'widget.preview',
  'widget.ready',
  'widget.request',
  'widget.status',
]);

/**
 * 需要 requestId 才能被关联的响应帧（widget -> host）。
 * widget.status / widget.ready 是广播帧：status 只报告 widget 自身状态，不是对某个
 * 在飞请求的应答，缺 requestId 也必须接受（否则宿主永远收不到 widget 的状态变化）。
 */
const WIDGET_RESPONSE_TYPES: ReadonlySet<string> = new Set<WidgetToHostType>([
  'widget.handshake.ack',
  'widget.handshake.error',
  'widget.request',
]);

function asRecord(value: unknown): null | Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** 普通对象（字面量 / Object.create(null)）：只有这种结构才会被逐键处理。 */
function isPlainRecord(value: Record<string, unknown>): boolean {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function asString(value: unknown): null | string {
  return typeof value === 'string' && value !== '' ? value : null;
}

function asNullableString(value: unknown): null | string | undefined {
  if (value === null) return null;
  if (typeof value === 'string') return value;
  return undefined;
}

function parseEnvelope(
  value: unknown,
  knownTypes: ReadonlySet<string>,
  requireRequestId: ReadonlySet<string>,
): null | { payload?: Record<string, unknown>; requestId?: string; type: string } {
  const record = asRecord(value);
  if (!record) return null;
  if (record.ns !== SUPPORT_WIDGET_NAMESPACE) return null;
  const version = asString(record.v);
  if (version === null || !SUPPORT_WIDGET_SUPPORTED_VERSIONS.includes(version)) {
    return null;
  }
  const type = asString(record.type);
  if (type === null || !knownTypes.has(type)) return null;
  const rawRequestId = record.requestId;
  let requestId: string | undefined;
  if (rawRequestId !== undefined) {
    const parsed = asString(rawRequestId);
    if (parsed === null) return null;
    requestId = parsed;
  }
  if (requireRequestId.has(type) && requestId === undefined) return null;
  const rawPayload = record.payload;
  if (rawPayload !== undefined && asRecord(rawPayload) === null) return null;
  return {
    type,
    ...(requestId === undefined ? {} : { requestId }),
    ...(rawPayload === undefined
      ? {}
      : { payload: rawPayload as Record<string, unknown> }),
  };
}

/** 校验 widget -> host 帧（含 requestId 关联要求）。 */
export function parseWidgetToHost(value: unknown): null | WidgetToHostMessage {
  const parsed = parseEnvelope(value, WIDGET_TYPES, WIDGET_RESPONSE_TYPES);
  if (!parsed) return null;
  return {
    ns: SUPPORT_WIDGET_NAMESPACE,
    type: parsed.type as WidgetToHostType,
    v: SUPPORT_WIDGET_PROTOCOL_VERSION,
    ...(parsed.requestId === undefined ? {} : { requestId: parsed.requestId }),
    ...(parsed.payload === undefined ? {} : { payload: parsed.payload }),
  };
}

/** 校验 host -> widget 帧。 */
export function parseHostToWidget(value: unknown): null | HostToWidgetMessage {
  const parsed = parseEnvelope(value, HOST_TYPES, new Set());
  if (!parsed) return null;
  return {
    ns: SUPPORT_WIDGET_NAMESPACE,
    type: parsed.type as HostToWidgetType,
    v: SUPPORT_WIDGET_PROTOCOL_VERSION,
    ...(parsed.requestId === undefined ? {} : { requestId: parsed.requestId }),
    ...(parsed.payload === undefined ? {} : { payload: parsed.payload }),
  };
}

/** 页面上下文结构校验；不含副作用，companyVerified=true 视为违规（浏览器不可自证）。 */
export function isPageContext(value: unknown): value is SupportPageContext {
  const record = asRecord(value);
  if (!record) return false;
  if (record.companyVerified === true) return false;
  if (asString(record.collectedAt) === null) return false;
  if (typeof record.pageInstanceId !== 'string' || !isUuid(record.pageInstanceId)) return false;
  // 稳定页面标识：URL/查询串/空白不是标识（后端 stableID 同口径）
  if (!isStablePageId(record.pageKey)) return false;
  // routeName 与 Go stableID 同口径：非空必须是标识（URL/查询串由后端判「页面路由标识错误」）；
  // undefined/null/空串 = 未提供，保持可选。
  if (record.routeName !== undefined && record.routeName !== null && record.routeName !== '') {
    if (!isStablePageId(record.routeName)) return false;
  }
  // 版本：非零规范十进制且 <= uint64（'0' 与越界都会被后端判 1901003）
  if (!isNonZeroUint64Decimal(record.version)) return false;
  if (record.companyId !== undefined && asNullableString(record.companyId) === undefined)
    return false;
  // 账期允许 null（未核实），但只要给了值就必须是 [12]xxx 年 + 合法月
  if (record.period !== undefined && record.period !== null && !isPagePeriod(record.period)) {
    return false;
  }
  if (record.filters !== undefined) {
    const filters = asRecord(record.filters);
    if (!filters) return false;
    for (const [key, entry] of Object.entries(filters)) {
      // 只允许后端 approvedFilters 白名单：凭据样式键与未批准键一律拒绝
      if (!isApprovedFilterKey(key)) return false;
      if (asString(entry) === null) return false;
    }
  }
  return true;
}

export function parseReadyPayload(
  payload: null | Record<string, unknown> | undefined,
): null | WidgetReadyPayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const protocolVersion = asString(record.protocolVersion);
  if (protocolVersion === null) return null;
  const raw = record.supportedProtocolVersions;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const supported: string[] = [];
  for (const item of raw) {
    const value = asString(item);
    if (value === null) return null;
    supported.push(value);
  }
  const widgetVersion = asString(record.widgetVersion);
  return {
    protocolVersion,
    supportedProtocolVersions: supported,
    ...(widgetVersion === null ? {} : { widgetVersion }),
  };
}

export function parseHandshakeAckPayload(
  payload: null | Record<string, unknown> | undefined,
): null | WidgetHandshakeAckPayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  if (typeof record.accepted !== 'boolean') return null;
  const protocolVersion = asString(record.protocolVersion);
  if (protocolVersion === null) return null;
  const principal = parsePrincipalRef(record.principal);
  if (principal === undefined) return null;
  return {
    accepted: record.accepted,
    protocolVersion,
    ...(principal === null ? {} : { principal }),
  };
}

function parsePrincipalRef(value: unknown): null | SupportPrincipalRef | undefined {
  if (value === undefined || value === null) return null;
  const record = asRecord(value);
  if (!record) return undefined;
  const id = asString(record.id);
  const orgId = asString(record.orgId);
  const role = asString(record.role);
  const scopeId = asString(record.scopeId);
  const name = typeof record.name === 'string' ? record.name : null;
  if (id === null || orgId === null || scopeId === null || name === null) return undefined;
  if (role !== 'admin' && role !== 'agent' && role !== 'customer' && role !== 'supervisor') return undefined;
  return { id, name, orgId, role, scopeId };
}

export function parseHandshakeErrorPayload(
  payload: null | Record<string, unknown> | undefined,
): null | WidgetHandshakeErrorPayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const reason = asString(record.reason);
  if (
    reason !== 'internal' &&
    reason !== 'protocol-version' &&
    reason !== 'schema' &&
    reason !== 'unauthorized'
  ) {
    return null;
  }
  const message = asString(record.message);
  return { reason, ...(message === null ? {} : { message }) };
}

export function parseStatusPayload(
  payload: null | Record<string, unknown> | undefined,
): null | WidgetStatusPayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const state = asString(record.state);
  if (
    state !== 'authenticated' &&
    state !== 'error' &&
    state !== 'idle' &&
    state !== 'unauthenticated'
  ) {
    return null;
  }
  const errorKind = asString(record.errorKind);
  const errorMessage = asString(record.errorMessage);
  const open = typeof record.open === 'boolean' ? record.open : undefined;
  const unread = typeof record.unread === 'number' ? record.unread : undefined;
  return {
    state,
    ...(errorKind === null ? {} : { errorKind }),
    ...(errorMessage === null ? {} : { errorMessage }),
    ...(open === undefined ? {} : { open }),
    ...(unread === undefined ? {} : { unread }),
  };
}

export function parseEventPayload(
  payload: null | Record<string, unknown> | undefined,
): null | WidgetEventPayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const kind = asString(record.kind);
  if (
    kind !== 'context.applied' &&
    kind !== 'conversation.changed' &&
    kind !== 'message.received'
  ) {
    return null;
  }
  const accepted = typeof record.accepted === 'boolean' ? record.accepted : undefined;
  const conversationId = asString(record.conversationId);
  const messageId = asString(record.messageId);
  const sequence = asString(record.sequence);
  return {
    kind,
    ...(accepted === undefined ? {} : { accepted }),
    ...(conversationId === null ? {} : { conversationId }),
    ...(messageId === null ? {} : { messageId }),
    ...(sequence === null ? {} : { sequence }),
  };
}

export function parseRequestPayload(
  payload: null | Record<string, unknown> | undefined,
): null | WidgetRequestPayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const capability = asString(record.capability);
  if (capability !== 'context' && capability !== 'token') return null;
  const reason = asString(record.reason);
  return { capability, ...(reason === null ? {} : { reason }) };
}

export function parseCapturePayload(
  payload: null | Record<string, unknown> | undefined,
): null | WidgetCapturePayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const phase = asString(record.phase);
  if (phase !== 'hide' && phase !== 'restore' && phase !== 'select') return null;
  return { phase };
}

/** 与消息附件上限一致：悬浮窗外的大图不超过 10MB。 */
const MAX_PREVIEW_IMAGE_BYTES = 10 * 1024 * 1024;

export function parsePreviewPayload(
  payload: null | Record<string, unknown> | undefined,
): null | WidgetPreviewPayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const phase = asString(record.phase);
  if (phase === 'close') return { phase };
  if (phase !== 'open') return null;
  const image = record.image;
  if (!(image instanceof Blob) || image.size <= 0 || image.size > MAX_PREVIEW_IMAGE_BYTES) return null;
  const mime = image.type || (typeof record.mime === 'string' ? record.mime : '');
  if (!mime.startsWith('image/')) return null;
  const name = typeof record.name === 'string' ? record.name.slice(0, 200) : '';
  return { phase, image, mime, ...(name === '' ? {} : { name }) };
}

/** 宿主确认已经在页面上浮出大图。 */
export function parsePreviewAckPayload(
  payload: null | Record<string, unknown> | undefined,
): null | { phase: 'open' } {
  const record = asRecord(payload ?? null);
  if (!record || record.phase !== 'open') return null;
  return { phase: 'open' };
}

/** 能力名只收短的小写标识，最多 16 个；其他内容忽略。 */
function parseCapabilities(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value
    .filter((item): item is string => typeof item === 'string' && /^[a-z][a-z0-9-]{0,31}$/u.test(item))
    .slice(0, 16);
}

/** 校验 host.handshake 载荷（协议版本 + uuid pageInstanceId + 可选上下文 + 源令牌）。 */
export function parseHandshakePayload(
  payload: null | Record<string, unknown> | undefined,
): null | HostHandshakePayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const protocolVersion = asString(record.protocolVersion);
  if (protocolVersion === null) return null;
  const pageInstanceId = asString(record.pageInstanceId);
  if (pageInstanceId === null || !isUuid(pageInstanceId)) return null;
  const context = record.context ?? null;
  if (context !== null) {
    if (!isPageContext(context)) return null;
    // 外层实例与 context 内的实例必须一致：否则合法 envelope 也能把上下文
    // 落进另一个 pageInstanceId 的键（服务端按 key(会话,客户,实例) 隔离）。
    if (context.pageInstanceId !== pageInstanceId) return null;
  }
  const sourceToken = asNullableString(record.sourceToken ?? null);
  if (sourceToken === undefined) return null;
  const capabilities = parseCapabilities(record.capabilities);
  return {
    ...(capabilities === undefined ? {} : { capabilities }),
    context: context as null | SupportPageContext,
    pageInstanceId,
    protocolVersion,
    sourceToken,
  };
}

/** 校验 host.context 载荷（版本必须单调十进制，实例必须 uuid）。 */
export function parseContextPayload(
  payload: null | Record<string, unknown> | undefined,
): null | HostContextPayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const pageInstanceId = asString(record.pageInstanceId);
  if (pageInstanceId === null || !isUuid(pageInstanceId)) return null;
  if (!isPageContext(record.context)) return null;
  const version = asString(record.version);
  if (version === null || !isCanonicalDecimal(version)) return null;
  if (record.context.pageInstanceId !== pageInstanceId) return null;
  if (record.context.version !== version) return null;
  return { context: record.context, pageInstanceId, version };
}

export function parseOpenPayload(
  payload: null | Record<string, unknown> | undefined,
): null | HostOpenPayload {
  const record = asRecord(payload ?? null);
  if (!record || typeof record.open !== 'boolean') return null;
  return { open: record.open };
}

export function parseSessionPayload(
  payload: null | Record<string, unknown> | undefined,
): null | HostSessionPayload {
  const record = asRecord(payload ?? null);
  if (!record) return null;
  const sourceToken = asString(record.sourceToken);
  if (sourceToken === null) return null;
  const reason = record.reason === 'login' ? 'login' : 'switch';
  return { reason, sourceToken };
}

export function createHostMessage<TType extends HostToWidgetType>(
  type: TType,
  payload: Record<string, unknown>,
  requestId?: string,
): HostToWidgetMessage {
  return {
    ns: SUPPORT_WIDGET_NAMESPACE,
    payload,
    type,
    v: SUPPORT_WIDGET_PROTOCOL_VERSION,
    ...(requestId === undefined ? {} : { requestId }),
  };
}

export function createWidgetMessage<TType extends WidgetToHostType>(
  type: TType,
  payload?: Record<string, unknown>,
  requestId?: string,
): WidgetToHostMessage {
  return {
    ns: SUPPORT_WIDGET_NAMESPACE,
    ...(payload === undefined ? {} : { payload }),
    type,
    v: SUPPORT_WIDGET_PROTOCOL_VERSION,
    ...(requestId === undefined ? {} : { requestId }),
  };
}

/**
 * 供日志/调试面板使用：**递归**抹掉凭据类字段，避免把凭据写进控制台、宿主日志或截图。
 *
 * 之前只脱敏 payload 第一层，`payload.context.filters` 里的 token/password/secret/cookie/
 * authorization 会原样进入宿主日志事件。这里改为按结构递归：
 * - 键名命中凭据模式（token/password/secret/cookie/authorization）→ 值替换为 REDACTED（保留 null 语义）；
 * - 键名命中协议帧的**自由文案**字段（`message`/`errorMessage`，见 FREE_TEXT_KEYS）→ 同样遮蔽：
 *   这些字段是按帧语义定义的"对端给的可读文本"，可以是任意内容（甚至秘密），不是业务数据；
 *   业务文本（如 filters.keyword、event.messageId）走具名字段，不受影响；
 * - 其它键递归处理：数组、嵌套对象都覆盖；
 * - requestId 遮蔽，errorKind 仅保留已知类别；其它业务文本不按值内容误删；
 * - 超出协议已知深度（异常深层结构）时整体替换为 REDACTED：宁可不打印，也不泄漏。
 */
const REDACTED_PLACEHOLDER = 'REDACTED';

/**
 * 协议帧结构里按语义属于"自由文案"的键（精确键名，不做子串匹配）：
 * widget.handshake.error 的 message、widget.status 的 errorMessage。
 * 其值由对端进程给任意文本，日志里只保留键名与遮蔽标记。
 */
const FREE_TEXT_KEYS: ReadonlySet<string> = new Set(['errorMessage', 'message']);

// 仅用于日志副本：client 的错误分类及 WidgetSession 的固定分类。
// 未知/未来类别保留字段但遮蔽原值，不改变真实状态帧和业务处理。
const LOG_ERROR_KINDS: ReadonlySet<string> = new Set([
  'auth',
  'capacity',
  'conflict',
  'expired-cursor',
  'forbidden',
  'identity',
  'internal',
  'invalid',
  'network',
  'not-found',
  'protocol',
  'realtime',
  'sync',
  'timeout',
  'too-large',
  'unauthorized',
  'unknown',
]);

/** 协议帧结构固定很浅；超过该深度说明是异常结构，直接整体遮蔽。 */
const MAX_REDACT_DEPTH = 8;

export function redactProtocolMessage(value: unknown): unknown {
  return redactValue(value, 0);
}

function redactValue(value: unknown, depth: number): unknown {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    if (depth >= MAX_REDACT_DEPTH) return REDACTED_PLACEHOLDER;
    return value.map((entry) => redactValue(entry, depth + 1));
  }
  const record = asRecord(value);
  if (!record) return value;
  // 非普通对象（Date/Map/Set/类实例/Error…）不是协议结构：整体遮蔽，绝不原样打印
  if (!isPlainRecord(record)) return REDACTED_PLACEHOLDER;
  if (depth >= MAX_REDACT_DEPTH) return REDACTED_PLACEHOLDER;
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(record)) {
    if (key === 'errorKind') {
      out[key] =
        entry === null ||
        entry === undefined ||
        (typeof entry === 'string' && LOG_ERROR_KINDS.has(entry))
          ? entry
          : REDACTED_PLACEHOLDER;
      continue;
    }
    if (isCredentialKey(key)) {
      out[key] = entry === null || entry === undefined ? entry : REDACTED_PLACEHOLDER;
      continue;
    }
    if (key === 'requestId' || FREE_TEXT_KEYS.has(key)) {
      // 关联 ID 仅供真实协议匹配；日志副本不保留可由对端任意指定的原值。
      // 自由文案同样只遮蔽值、保留键名。
      out[key] = entry === null || entry === undefined ? entry : REDACTED_PLACEHOLDER;
      continue;
    }
    out[key] = redactValue(entry, depth + 1);
  }
  return out;
}
